import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaBell, FaCalendar, FaChevronRight, FaClock } from "react-icons/fa";

import "./header.css";
import "../modal/modal.css";
import "../../pages/adm/admPatrocinadoresList.css";

import Logo from "../../assets/SBF-Logo-1.png";
import iconeMenu from "../../assets/header/icone_menu.png";

import Modal from "../modal/modal";
import ModalPesquisa from "../modal/modalPesquisa";

import useApp from "../../hooks/useAppContext";
import useMenu from "../../hooks/useMenuContext";
import useAuth from "../../hooks/useAuthContext";
import { formatarDiaHora } from "../../services/conversaoTimer";

function tipoNotificacao(n) {
  return n?.comunicado?.tipo === "chat" ? "chat" : "geral";
}

function dataNotificacao(n) {
  return new Date(n?.comunicado?.created_at || n?.created_at || 0).getTime();
}

/**
 * Agrupa mensagens de chat do mesmo remetente em um único item (o mais recente).
 * Avisos gerais permanecem individuais.
 */
function agruparNotificacoesModal(lista) {
  const ordenada = [...(lista || [])].sort(
    (a, b) => dataNotificacao(b) - dataNotificacao(a)
  );
  const resultado = [];
  const chatsIncluidos = new Set();

  for (const n of ordenada) {
    if (tipoNotificacao(n) !== "chat") {
      resultado.push({
        ...n,
        grupoIds: [n.id],
        grupoCount: 1,
      });
      continue;
    }

    const idUsuario = n?.comunicado?.id_usuario;
    if (idUsuario == null) {
      resultado.push({
        ...n,
        grupoIds: [n.id],
        grupoCount: 1,
      });
      continue;
    }

    if (chatsIncluidos.has(idUsuario)) continue;
    chatsIncluidos.add(idUsuario);

    const grupo = ordenada.filter(
      (item) =>
        tipoNotificacao(item) === "chat" &&
        item?.comunicado?.id_usuario === idUsuario
    );
    const maisRecente = grupo[0];
    const temNaoLida = grupo.some((item) => Number(item.status) === 0);

    resultado.push({
      ...maisRecente,
      status: temNaoLida ? 0 : Number(maisRecente.status),
      grupoIds: grupo.map((item) => item.id),
      grupoCount: grupo.length,
    });
  }

  return resultado;
}

const Header = () => {
  const {
    notificacoesUsuario,
    visualizarNotificacao,
    consultarNotificacoes,
    verificaPesquisa,
    pesquisasUsuario,
    tipoUsuario,
  } = useApp();
  const { toggleMenu } = useMenu();
  const { usuario } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [showModalPesquisa, setShowModalPesquisa] = useState(false);
  const [pesquisa, setPesquisa] = useState(null);
  const marcadasRef = useRef(new Set());

  const listaCompleta = useMemo(() => {
    return [...(notificacoesUsuario || [])].sort(
      (a, b) => dataNotificacao(b) - dataNotificacao(a)
    );
  }, [notificacoesUsuario]);

  const listaModal = useMemo(
    () => agruparNotificacoesModal(listaCompleta),
    [listaCompleta]
  );

  const quantidadeNotificacoes = useMemo(
    () => listaCompleta.filter((n) => Number(n.status) === 0).length,
    [listaCompleta]
  );

  const marcarComoVisualizadas = useCallback(
    (itens) => {
      const pendentes = (itens || []).filter(
        (n) => Number(n.status) === 0 && !marcadasRef.current.has(n.id)
      );
      if (!pendentes.length) return;

      pendentes.forEach((n) => {
        marcadasRef.current.add(n.id);
        visualizarNotificacao(n.id);
      });
    },
    [visualizarNotificacao]
  );

  const handleModalNotificacao = useCallback(() => {
    setShowModal((aberto) => {
      const proximo = !aberto;
      if (proximo) {
        consultarNotificacoes({ force: true });
      }
      return proximo;
    });
  }, [consultarNotificacoes]);

  const handleModalPesquisa = () => {
    setShowModalPesquisa(!showModalPesquisa);
  };

  useEffect(() => {
    if (!showModal) return;
    // Marca todas as originais (incluindo as agrupadas/ocultas)
    marcarComoVisualizadas(listaCompleta);
  }, [showModal, listaCompleta, marcarComoVisualizadas]);

  useEffect(() => {
    if (usuario?.tipo === "convidado" && usuario?.tipo === "patrocinador") {
      verificaPesquisa();
    }
  }, [usuario, verificaPesquisa]);

  useEffect(() => {
    if (
      pesquisasUsuario.length &&
      usuario &&
      usuario?.tipo &&
      usuario?.tipo !== "patrocinador"
    ) {
      setPesquisa(pesquisasUsuario[0]);
      setShowModalPesquisa(true);
    }
  }, [pesquisasUsuario, usuario]);

  return (
    <div id="container-header">
      <Modal
        titulo="Notificações"
        to="/private/notificacoes"
        labelTo="Ver todas"
        show={showModal}
        toggleModal={handleModalNotificacao}
        dialogClassName="modal-notificacoes-header"
        footerClassName="modal-notificacoes-footer"
      >
        <div className="header-notif-modal">
          {listaModal.length === 0 ? (
            <div className="header-notif-empty">
              <p className="header-notif-empty-title">Nenhuma notificação</p>
              <p>Quando houver avisos ou mensagens, eles aparecerão aqui.</p>
            </div>
          ) : (
            <ul className="header-notif-list" aria-label="Notificações recentes">
              {listaModal.map((n) => {
                const comunicado = n.comunicado || {};
                const isChat = tipoNotificacao(n) === "chat";
                const isNaoLida = Number(n.status) === 0;
                const grupoCount = n.grupoCount || 1;
                const dataCriado = formatarDiaHora(
                  comunicado.created_at || n.created_at
                );
                const destinoChat = comunicado.id_usuario
                  ? `/private/chat/${comunicado.id_usuario}`
                  : null;
                const texto =
                  isChat && grupoCount > 1
                    ? `${grupoCount} mensagens no chat`
                    : comunicado.texto || "—";

                const conteudo = (
                  <>
                    <div className="header-notif-icon" aria-hidden>
                      <FaBell size={12} />
                    </div>
                    <div className="header-notif-body">
                      <div className="header-notif-top">
                        <span
                          className={`apl-tag-type${
                            isChat ? " apl-tag-type--mm" : ""
                          }`}
                        >
                          {isChat ? "Mensagem" : "Aviso geral"}
                        </span>
                        {isChat && grupoCount > 1 ? (
                          <span className="header-notif-grupo-count">
                            {grupoCount}
                          </span>
                        ) : null}
                        {isNaoLida ? (
                          <span className="header-notif-badge">Nova</span>
                        ) : null}
                      </div>
                      <p className="header-notif-texto">{texto}</p>
                      {isChat && grupoCount > 1 && comunicado.texto ? (
                        <p className="header-notif-texto-secundario">
                          Última: {comunicado.texto}
                        </p>
                      ) : null}
                      <div className="header-notif-meta">
                        <span>
                          <FaClock size={10} aria-hidden />
                          {dataCriado || "—"}
                        </span>
                        {isChat && destinoChat ? (
                          <span className="header-notif-cta">
                            Abrir conversa
                            <FaChevronRight size={9} aria-hidden />
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </>
                );

                return (
                  <li key={isChat ? `chat-${comunicado.id_usuario || n.id}` : n.id}>
                    {isChat && destinoChat ? (
                      <Link
                        to={destinoChat}
                        className={`header-notif-item${
                          isNaoLida ? " is-unread" : ""
                        }`}
                        onClick={() => setShowModal(false)}
                      >
                        {conteudo}
                      </Link>
                    ) : (
                      <div
                        className={`header-notif-item header-notif-item--static${
                          isNaoLida ? " is-unread" : ""
                        }`}
                      >
                        {conteudo}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal>

      <ModalPesquisa
        titulo="Pesquisa"
        show={showModalPesquisa}
        toggleModal={handleModalPesquisa}
        pesquisa={pesquisa}
        tipoUsuario={tipoUsuario}
      />

      <button
        type="button"
        id="icone-menu"
        className="header-menu-botao"
        onClick={toggleMenu}
        aria-label="Abrir menu"
      >
        <img src={iconeMenu} alt="" />
      </button>

      <Link to="/" className="small-box-footer">
        <img src={Logo} alt="Logo" id="logo-header" />
      </Link>

      {usuario?.tipo !== "recepcao" ? (
        <>
          <Link to="/private/agenda" className="small-box-footer">
            <div title="Minha agenda" id="agenda-header">
              <FaCalendar />
            </div>
          </Link>
          <button
            type="button"
            id="notificacoes-header"
            title="Notificações"
            aria-label={
              quantidadeNotificacoes > 0
                ? `${quantidadeNotificacoes} notificações não lidas`
                : "Notificações"
            }
            onClick={handleModalNotificacao}
          >
            <FaBell
              style={{
                color: quantidadeNotificacoes > 0 ? "#dc2626" : "#018A38",
              }}
            />
            {quantidadeNotificacoes > 0 ? (
              <span className="header-notif-count" aria-hidden>
                {quantidadeNotificacoes > 99 ? "99+" : quantidadeNotificacoes}
              </span>
            ) : null}
          </button>
        </>
      ) : null}
    </div>
  );
};

export default Header;
